import { EventCardLayouts } from '@/types/gql.generated';
import { Box, Flex, Icon } from '@/ui';
import {
  CARD_COLOR_OPTIONS,
  type CardColorMode,
  toCardColorMode,
  toExperienceColorMode,
} from '../cardColorMode';
import {
  SidebarCard,
  SidebarIconChip,
  SidebarRow,
  SidebarRowLabel,
  SidebarSection,
  SidebarSubLabel,
} from '../SidebarKit';
import { SidebarRowSwitch } from '../SidebarRowSwitch';
import {
  SegmentedControl,
  type SegmentedOption,
} from '../StylePanel/SegmentedControl';
import { useCustomizeControl } from '../useCustomizeControl';
import { TextSizeSelect } from './TextSizeSelect';
import { TimeFormatSelect } from './TimeFormatSelect';

// The planner grid event display type is a persisted, block-ready
// EventCardLayouts value (one component per type — see event/GridEvent*).
const DISPLAY_OPTIONS: SegmentedOption<EventCardLayouts>[] = [
  { label: 'Card', value: EventCardLayouts.GridEventCard },
  { label: 'Line', value: EventCardLayouts.GridEventLine },
  { label: 'Bullet', value: EventCardLayouts.GridEventBullet },
  { label: 'Text', value: EventCardLayouts.GridEventText },
];

type Props = {
  supportsDate: boolean;
  supportsDescription: boolean;
  supportsEmoji: boolean;
  supportsLocation: boolean;
  // The card/line/bullet/text picker only shows where the type is selectable
  // (multi-card-option layouts — the planner grids).
  showDisplayType: boolean;
  // Text size is a calendar-only control (Month / Week grids).
  showTextSize: boolean;
  // Color category + name-truncation options — shown on the planner grids and
  // the list layouts.
  showEventOptions: boolean;
  // The color legend is only meaningful on the calendar grids (and printables),
  // so it's gated separately from the other event options.
  showColorLegend: boolean;
  // Bold/Light card fill, shown only where Card is the active display type.
  // Whole-schedule (colorMode) rather than per-view, and only consequential on
  // layouts that draw cards — hence the scope caption on its label.
  showCardColorMode: boolean;
};

export const ShowOnViewSection = ({
  supportsDate,
  supportsDescription,
  supportsEmoji,
  supportsLocation,
  showDisplayType,
  showTextSize,
  showEventOptions,
  showColorLegend,
  showCardColorMode,
}: Props) => {
  const { register, registerToggle } = useCustomizeControl();

  // Bound once so the rows below can read a sibling's saved state: End time and
  // the colour legend are each meaningless without the toggle they depend on.
  const startTime = registerToggle('child', 'eventCardDateEnabled');
  const endTime = registerToggle('child', 'eventCardEndTimeEnabled');
  const category = registerToggle('child', 'eventCardCategoryEnabled');
  const colorLegend = registerToggle('child', 'colorLegendEnabled');

  return (
    <SidebarSection label="Show on this view">
      <Flex direction="column" gap={2}>
        {showDisplayType && (
          <SegmentedControl
            options={DISPLAY_OPTIONS}
            {...register('child', 'eventCardLayoutId')}
          />
        )}

        {showCardColorMode && (
          <Box>
            <SidebarSubLabel scope="For views with cards">
              Card style
            </SidebarSubLabel>
            <SegmentedControl
              options={CARD_COLOR_OPTIONS}
              {...register<'colorMode', CardColorMode>(
                'experience',
                'colorMode',
                { toControl: toCardColorMode, toField: toExperienceColorMode }
              )}
            />
          </Box>
        )}

        {/* overflow="visible" so the Time and Text size dropdowns aren't clipped by
            the card, which otherwise hides everything below the row they open from.
            Safe here because no row in this card paints a background that would
            need clipping to the card's rounded corners. */}
        <SidebarCard overflow="visible">
          {supportsEmoji && (
            <SidebarRow>
              <SidebarRowLabel label="Emoji" />
              <SidebarRowSwitch
                {...registerToggle('child', 'eventCardEmojiEnabled')}
              />
            </SidebarRow>
          )}
          {supportsDate && (
            <SidebarRow>
              <SidebarRowLabel label="Start time" />
              <SidebarRowSwitch {...startTime} />
            </SidebarRow>
          )}
          {supportsDate && (
            <SidebarRow>
              <SidebarRowLabel label="End time" />
              <SidebarRowSwitch
                {...endTime}
                // End time is meaningless without a start time, so gate it on the
                // Start time toggle.
                isChecked={endTime.isChecked && startTime.isChecked}
                isDisabled={!startTime.isChecked}
              />
            </SidebarRow>
          )}
          {supportsDate && (
            <SidebarRow>
              <Flex align="center" flexShrink={0} gap={2.5}>
                <SidebarIconChip size="26px">
                  <Icon color="ink.soft" icon="Clock" size={3.5} />
                </SidebarIconChip>
                <SidebarRowLabel label="Time" />
              </Flex>
              <TimeFormatSelect />
            </SidebarRow>
          )}
          {supportsLocation && (
            <SidebarRow>
              <SidebarRowLabel label="Location" />
              <SidebarRowSwitch
                {...registerToggle('child', 'eventCardLocationEnabled')}
              />
            </SidebarRow>
          )}
          {supportsDescription && (
            <SidebarRow>
              <SidebarRowLabel label="Description" />
              <SidebarRowSwitch
                {...registerToggle('child', 'eventCardDescriptionEnabled')}
              />
            </SidebarRow>
          )}
          {showEventOptions && (
            <SidebarRow>
              <Flex align="center" gap={2.5} minW={0}>
                <SidebarIconChip size="26px">
                  <Icon color="ink.soft" icon="Palette" size={3.5} />
                </SidebarIconChip>
                <SidebarRowLabel label="Category color" />
              </Flex>
              <SidebarRowSwitch flexShrink={0} ml={2} {...category} />
            </SidebarRow>
          )}
          {showColorLegend && (
            <SidebarRow>
              <SidebarRowLabel label="Show color legend" />
              <SidebarRowSwitch
                {...colorLegend}
                // A legend of category colors reads as noise when the events
                // aren't painted with them, so gate it on Category color.
                isChecked={colorLegend.isChecked && category.isChecked}
                isDisabled={!category.isChecked}
              />
            </SidebarRow>
          )}
          {/* No "Show labels" row: per-view label display is deferred to
              post-launch. eventCardLabelsEnabled still drives rendering. */}
          {showEventOptions && (
            <SidebarRow>
              <Flex align="center" gap={2.5} minW={0}>
                <SidebarIconChip size="26px">
                  <Icon color="ink.soft" icon="Scissors" size={3.5} />
                </SidebarIconChip>
                <SidebarRowLabel label="Truncate long titles" />
              </Flex>
              <SidebarRowSwitch
                flexShrink={0}
                ml={2}
                {...registerToggle('child', 'eventCardTruncateNamesEnabled')}
              />
            </SidebarRow>
          )}
          {showTextSize && (
            <SidebarRow>
              <Flex align="center" flexShrink={0} gap={2.5}>
                <SidebarIconChip size="26px">
                  <Icon color="ink.soft" icon="LetterText" size={3.5} />
                </SidebarIconChip>
                <SidebarRowLabel label="Text size" />
              </Flex>
              <TextSizeSelect />
            </SidebarRow>
          )}
        </SidebarCard>
      </Flex>
    </SidebarSection>
  );
};
